import axios from "axios";
import classNames from "classnames";
import axiosInstance from "helpers/api";
import { Table } from "react-bootstrap";
import { useEffect, useState } from "react";
import { flexRender } from "@tanstack/react-table";
import { AdvanceTableProps } from "interfaces/table";
import { useAdvanceTableContext } from "providers/AdvanceTableProvider";

interface Collaborator {
  id: string;
  name: string;
  email: string;
  role?: string;
  [key: string]: any;
}

const AdvanceTable = ({
  hasFooter,
  tableProps,
  rowClassName,
  bodyClassName,
  headerClassName,
}: AdvanceTableProps) => {
  const table = useAdvanceTableContext();
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const { getRowModel, getFlatHeaders, getFooterGroups } = table;

  const fetchCollaborators = async () => {
    try {
      const response = await axiosInstance.get("/collaborators");
      setCollaborators(response.data.data.collaborators);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.log(error.response?.data?.message || 'An error occurred');
      } else {
        console.log('An unexpected error occurred');
      }
    }
  };

  useEffect(() => {
    fetchCollaborators();
  }, []);

  return (
    <div className="scrollbar ms-n1 ps-1" style={{ minHeight: "270px" }}>
      <Table {...tableProps}>
        <thead className={headerClassName}>
          <tr>
            {getFlatHeaders().map((header) => {
              return (
                <th
                  key={header.id}
                  {...header.column.columnDef.meta?.headerProps}
                  className={classNames(
                    header.column.columnDef.meta?.headerProps?.className,
                    {
                      sort: header.column.getCanSort(),
                      desc: header.column.getIsSorted() === "desc",
                      asc: header.column.getIsSorted() === "asc",
                    }
                  )}
                  onClick={header.column.getToggleSortingHandler()}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className={bodyClassName}>
          {getRowModel().rows.length > 0 ? (
            getRowModel().rows.map((row) => (
              <tr key={row.id} className={rowClassName}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} {...cell.column.columnDef.meta?.cellProps}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={6} style={{ textAlign: "center", padding: "8px" }}>
                No data found
              </td>
            </tr>
          )}
        </tbody>
        {hasFooter && (
          <tfoot>
            {getFooterGroups().map((footerGroup) => (
              <tr key={footerGroup.id} className="border-0 border-translucent">
                {footerGroup.headers.map((header) => {
                  return (
                    <th
                      key={header.id}
                      {...header.column.columnDef.meta?.footerProps}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.footer,
                            header.getContext()
                          )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </tfoot>
        )}
      </Table>
    </div>
  );
};

export default AdvanceTable;
